import styled from "@emotion/styled"
import { PHeading } from "@porsche-design-system/components-react"
import { spacingStaticSm, spacingStaticMd, spacingStaticLg } from "@porsche-design-system/components-react/emotion"
import type { PageTeaserView } from "@slfinrtl/ui-bff-routes"
import type { JSX, PropsWithChildren } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { useMediaQueryWidth } from "src/hooks"
import { PictureResponsive } from "../pictureResponsive/PictureResponsive"
import { Skeleton } from "../skeleton/Skeleton"
import { PageTeaserStickyHeader } from "./PageTeaserStickyHeader"
import { PageTeaserTags } from "./PageTeaserTags"
import { isPageTeaserSkeletonMarker } from "./pageTeaserView.utils"

interface PageTeaserProps {
	actionButton?: JSX.Element
	actionButtonAlignmentMobile?: "horizontal" | "vertical"
	stickyHeader?: boolean
	view: PageTeaserView
}

export const PageTeaser = (props: PropsWithChildren<PageTeaserProps>) => {
	const isDesktop = useMediaQueryWidth({ min: "m" })
	const { actionButton, stickyHeader, view } = props

	return (
		<>
			{isDesktop ? <PageTeaserDesktop {...props} /> : <PageTeaserMobile {...props} />}
			{stickyHeader && <PageTeaserStickyHeader actionButton={actionButton} view={view} />}
		</>
	)
}

const PageTeaserDesktop = ({ actionButton, children, view }: PropsWithChildren<PageTeaserProps>) => (
	<WrapperDesktop $hasActions={!!actionButton} data-testid="page-teaser">
		{view.image ? (
			<Teaser>
				<Image heading={view.heading} image={view.image} />
				<Headline {...view} />
			</Teaser>
		) : (
			<Headline {...view} />
		)}
		{actionButton}
		{children && <ChildrenDesktop>{children}</ChildrenDesktop>}
	</WrapperDesktop>
)

const PageTeaserMobile = ({
	actionButton,
	actionButtonAlignmentMobile = "vertical",
	children,
	view,
}: PropsWithChildren<PageTeaserProps>) => {
	const isHorizontalAction = actionButtonAlignmentMobile === "horizontal" && !!actionButton

	return (
		<WrapperMobile data-testid="page-teaser">
			{view.image && <Image heading={view.heading} image={view.image} fullWidth />}
			{isHorizontalAction ? (
				<MobileHeadlineWithAction>
					<Headline {...view} />
					<ActionButtonSlot>{actionButton}</ActionButtonSlot>
				</MobileHeadlineWithAction>
			) : (
				<>
					<Headline {...view} />
					{actionButton}
				</>
			)}
			{children && <ChildrenMobile>{children}</ChildrenMobile>}
		</WrapperMobile>
	)
}

const Image = ({
	fullWidth,
	heading,
	image,
}: {
	fullWidth?: boolean
	heading: PageTeaserView["heading"]
	image: NonNullable<PageTeaserView["image"]>
}) => (
	<ImageItem $fullWidth={!!fullWidth}>
		{isPageTeaserSkeletonMarker(image) ? (
			<Skeleton.Picture />
		) : (
			<PictureResponsive
				picture={image}
				alt={typeof heading === "string" ? heading : ""}
				sizes={{ base: "full", m: "one-eighth", s: "half" }}
			/>
		)}
	</ImageItem>
)

const Headline = ({ context, heading, tags }: PageTeaserView) => (
	<HeadingWrapper>
		<div>
			{isPageTeaserSkeletonMarker(context) ? (
				<Skeleton.Heading size="sm" $width="50%" />
			) : (
				context && (
					<PHeading size="sm" weight="bold" tag="h2">
						{context}
					</PHeading>
				)
			)}
			{isPageTeaserSkeletonMarker(heading) ? (
				<Skeleton.Heading size="xl" $width="70%" />
			) : (
				<PHeading size="xl" tag="h1">
					{heading}
				</PHeading>
			)}
		</div>
		<PageTeaserTags view={tags} />
	</HeadingWrapper>
)

const WrapperDesktop = styled("header", { shouldForwardProp })<{ $hasActions?: boolean }>`
	display: grid;
	grid-template-columns: ${({ $hasActions }) => ($hasActions ? "1fr auto" : "1fr")};
	gap: ${spacingStaticMd};
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	padding: ${spacingStaticLg} 0;
`

const ChildrenDesktop = styled.div`
	grid-column: 1 / -1;
`

const WrapperMobile = styled.header`
	display: flex;
	flex-direction: column;
	align-items: start;
	gap: ${spacingStaticSm};
	padding: ${spacingStaticMd} 0;
`

const ChildrenMobile = styled.div`
	align-self: stretch;
	margin-top: ${spacingStaticSm};
`

const MobileHeadlineWithAction = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: ${spacingStaticSm};
	align-self: stretch;
`

const ActionButtonSlot = styled.div`
	flex-shrink: 0;
`

const HeadingWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
	min-width: 0;
`

const Teaser = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticMd};
`

const ImageItem = styled("div", { shouldForwardProp })<{ $fullWidth: boolean }>`
	min-width: 160px;
	width: ${(props) => (props.$fullWidth ? "100%" : "160px")};
`
